import { AlertCircle, Check, ChevronsUp, Diamond, Flame, ShieldCheck } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { Task } from '../../api/types';
import { Badge } from '../../components/ui/Badge';
import { cn } from '../../lib/cn';
import { formatDateFull, formatDue } from '../../lib/dates';
import { APPROVAL_LABELS, APPROVAL_TONES, dueState } from './taskUtils';

/** Номер задачі «Т-125» — моноширинний, у фіксованій колонці «поля» переліку. */
export function TaskKey({ task, className }: { task: Pick<Task, 'key'>; className?: string }) {
  return <span className={cn('key shrink-0 text-meta text-fg-3', className)}>{task.key}</span>;
}

/** Строк: колір лише для стану (прострочено — червоний з іконкою; сьогодні — акцент тексту). */
export function DueLabel({ task, className, showEmpty = true, plain = false }: { task: Pick<Task, 'dueOn' | 'status'>; className?: string; showEmpty?: boolean; plain?: boolean }) {
  // plain — лише дата, без кольору стану: прострочення вже назване поруч (рядок попереджень на картці дошки).
  const st = plain && dueState(task) === 'overdue' ? 'later' : dueState(task);
  if (!task.dueOn) return showEmpty ? <span className={cn('text-meta text-fg-3', className)}>Без строку</span> : null;
  return (
    <span
      title={st === 'overdue' ? `Прострочено: строк ${formatDateFull(task.dueOn)}` : formatDateFull(task.dueOn)}
      className={cn(
        'tnum inline-flex items-center gap-1 text-meta whitespace-nowrap',
        st === 'overdue' && 'font-medium text-danger-fg',
        st === 'today' && 'font-medium text-fg',
        (st === 'soon' || st === 'later') && 'text-fg-2',
        st === 'done' && 'text-fg-3',
        className,
      )}
    >
      {st === 'overdue' && <AlertCircle className="size-3.5 shrink-0" aria-hidden strokeWidth={2} />}
      {st === 'overdue' && <span className="sr-only">Прострочено:</span>}
      {formatDue(task.dueOn)}
    </span>
  );
}

type CheckProps = ButtonHTMLAttributes<HTMLButtonElement> & { task: Pick<Task, 'status' | 'key' | 'title' | 'subtype' | 'approvalStatus'>; busy?: boolean };

/** Кругла кнопка завершення. Завершення не миттєве: відкриває діалог результату (правило фірми). */
export const CompleteToggle = forwardRef<HTMLButtonElement, CheckProps>(function CompleteToggle({ task, busy, className, ...rest }, ref) {
  const done = task.status === 'completed';
  const approval = task.subtype === 'approval';
  const label = done ? `Повторно відкрити ${task.key}` : approval ? `Ухвалити рішення щодо ${task.key}` : `Завершити ${task.key} з результатом`;
  return (
    <button
      ref={ref}
      type="button"
      aria-label={label}
      title={label}
      disabled={busy}
      className={cn(
        'group/check relative inline-flex size-[18px] shrink-0 items-center justify-center rounded-full border transition-colors',
        done ? 'border-success bg-success text-on-accent' : 'border-line-input text-transparent hover:border-success hover:text-success',
        approval && !done && 'rounded-xs',
        'disabled:opacity-50',
        'before:absolute before:-inset-2 before:content-[""]',
        className,
      )}
      {...rest}
    >
      {approval && !done ? (
        <ShieldCheck className="size-3 text-fg-3 group-hover/check:text-success" aria-hidden strokeWidth={2.25} />
      ) : (
        <Check className={cn('size-3', done && 'ers-check-pop')} aria-hidden strokeWidth={3} />
      )}
    </button>
  );
});

/**
 * Підпис попередження в рядку переліку. Коли область назви вузька (перелік поруч із відкритою карткою, багато колонок),
 * підпис ховається й лишається значок із підказкою — попередження не зникає й не обрізається. Працює лише всередині
 * рядка переліку (контейнер «title»); деінде — звичайний підпис.
 */
export const ALERT_LABEL = 'sm:@max-sm/title:sr-only';

export function TaskFlags({ task, compact, hideUrgent }: { task: Task; compact?: boolean; hideUrgent?: boolean }) {
  return (
    <>
      {task.urgent && !hideUrgent && (
        <Badge tone="warning" icon={<Flame className="size-3" aria-hidden />} title="Термінова задача" alert>
          <span className={ALERT_LABEL}>{compact ? 'Терм.' : 'Терміново'}</span>
        </Badge>
      )}
      {(task.priority === 'high' || task.priority === 'critical') && (
        <span title={task.priority === 'critical' ? 'Критичний пріоритет' : 'Високий пріоритет'} className="inline-flex items-center text-warning">
          <ChevronsUp className="size-4" aria-hidden />
          <span className="sr-only">{task.priority === 'critical' ? 'Критичний пріоритет' : 'Високий пріоритет'}</span>
        </span>
      )}
      {task.subtype === 'approval' && task.approvalStatus && (
        <Badge tone={APPROVAL_TONES[task.approvalStatus]} icon={<ShieldCheck className="size-3" aria-hidden />}>
          {APPROVAL_LABELS[task.approvalStatus]}
        </Badge>
      )}
      {task.subtype === 'milestone' && (
        <Badge tone="neutral" icon={<Diamond className="size-3" aria-hidden />}>
          Віха
        </Badge>
      )}
    </>
  );
}

/**
 * Кнопка «Позначити виконаною» в шапці картки (як в Asana). Завершення — з результатом (компактний діалог за правилом фірми);
 * для завершеної задачі — «Виконано», натискання пропонує відкрити знову.
 */
export function CompleteButton({ task, onClick, disabled }: { task: Pick<Task, 'status' | 'subtype' | 'key'>; onClick: () => void; disabled?: boolean }) {
  const done = task.status === 'completed';
  const approval = task.subtype === 'approval';
  const full = done ? 'Виконано' : approval ? 'Ухвалити рішення' : 'Позначити виконаною';
  const short = done ? 'Виконано' : approval ? 'Рішення' : 'Виконати';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-tour="task-complete"
      aria-label={done ? `${task.key} виконано. Відкрити знову` : `${full}: ${task.key}`}
      className={cn(
        'ers-press inline-flex h-[var(--control-sm)] shrink-0 items-center gap-1.5 rounded-sm border px-2 text-meta font-medium transition-colors disabled:opacity-50',
        done ? 'border-success-line bg-success-soft text-success-fg hover:bg-surface' : 'border-line-strong bg-surface text-fg-2 hover:border-success hover:text-success-fg',
      )}
    >
      {approval && !done ? <ShieldCheck className="size-3.5" aria-hidden /> : <Check className="size-3.5" aria-hidden strokeWidth={2.5} />}
      <span className="hidden sm:inline">{full}</span>
      <span className="sm:hidden">{short}</span>
    </button>
  );
}
